import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import apolloClient from 'api/apolloClient';
import {
  GENERATE_ATTR_FORM_BY_POLICY_ATTR_ANSWERS,
  GET_BC_FORMS,
  GET_BROWSING_PORTFOLIO_CONFIG_FORM,
  GET_COMPANY_FORM_INSTANCE,
  GET_CONTRIBUTION_FORM_INSTANCE,
  GET_ELECTION_FORM_BY_ID,
  GET_FORM_BY_ID,
  GET_FORM_INSTANCE,
  GET_FORM_INSTANCES,
  GET_MANAGE_DEPENDENTS_FORM,
  GET_RATE_RFQ_FORM,
  GET_WEB_FORM_INSTANCES,
  MARK_FORMS_AS_INACTIVE,
  SET_ANSWERS,
} from 'api/queries/formService';
import {
  FormAnswersObj,
  FormBcByProductLine,
  FormInstanceType,
  FormType,
  SetAnswersParamsType,
} from 'types/formService';

export const INITIAL_FORM_OBJECT: Readonly<FormObjectFromJSON> = Object.freeze({
  id: '',
  formTitle: '',
  sections: [],
});

export async function getWebFormInstances(enrollmentPeriodId: string): Promise<FormInstanceType[]> {
  return (
    await apolloClient.query<{ getWebFormInstances: FormInstanceType[] }>({
      query: GET_WEB_FORM_INSTANCES,
      fetchPolicy: 'no-cache',
      variables: { enrollmentPeriodId },
    })
  ).data.getWebFormInstances;
}

export async function requestFormById(id: string, bc?: FormBcByProductLine): Promise<FormObjectFromJSON> {
  return (
    await apolloClient.query<{ getFormById: FormObjectFromJSON }>({
      query: GET_FORM_BY_ID,
      fetchPolicy: 'no-cache',
      variables: { id, bc },
    })
  ).data.getFormById;
}

export async function getElectionFormById(
  id: string,
  carrierId: string,
  employeeClass: string,
  productId?: string,
): Promise<FormObjectFromJSON> {
  return (
    await apolloClient.query<{ getElectionFormById: FormObjectFromJSON }>({
      query: GET_ELECTION_FORM_BY_ID,
      fetchPolicy: 'no-cache',
      variables: { id, carrierId, clazz: employeeClass, productId },
    })
  ).data.getElectionFormById;
}

export async function generateAttrFormByPolicyAttrAnswers(
  formId: string,
  planAttr: string,
  sponsorId: string,
  policyAttrAnswers: Record<string, string>,
): Promise<FetchResult<{ generateAttrFormByPolicyAttrAnswers: FormObjectFromJSON }>> {
  return apolloClient.mutate<{ generateAttrFormByPolicyAttrAnswers: FormObjectFromJSON }>({
    mutation: GENERATE_ATTR_FORM_BY_POLICY_ATTR_ANSWERS,
    variables: { formId, planAttr, sponsorId, policyAttrAnswers },
  });
}

export async function requestCompanyFormInstance(enrollmentPeriodId: string): Promise<FormInstanceType | null> {
  return (
    await apolloClient.query<{ getCompanyInfoFormInstances: FormInstanceType[] }>({
      query: GET_COMPANY_FORM_INSTANCE,
      fetchPolicy: 'no-cache',
      variables: { enrollmentPeriodId },
    })
  ).data.getCompanyInfoFormInstances[0];
}

export async function requestEmployerFormInstance(
  formInstanceId: string,
): Promise<ApolloQueryResult<{ formInstanceById: FormInstanceType }>> {
  return apolloClient.query<{ formInstanceById: FormInstanceType }>({
    query: GET_FORM_INSTANCE,
    fetchPolicy: 'no-cache',
    variables: { formInstanceId },
  });
}

export async function requestSetAnswers(
  params: SetAnswersParamsType,
): Promise<FetchResult<{ setAnswers: FormInstanceType }>> {
  return apolloClient.mutate<{ setAnswers: FormInstanceType }>({
    mutation: SET_ANSWERS,
    fetchPolicy: 'no-cache',
    variables: params,
  });
}

export async function requestContributionFormInstance(enrollmentPeriodId: string): Promise<FormInstanceType | null> {
  return (
    await apolloClient.query<{ getContributionFormInstances: FormInstanceType[] }>({
      query: GET_CONTRIBUTION_FORM_INSTANCE,
      fetchPolicy: 'no-cache',
      variables: { enrollmentPeriodId },
    })
  ).data.getContributionFormInstances[0];
}

export async function requestBCForm(
  portfolioId: string,
  effectiveDate: string,
): Promise<ApolloQueryResult<{ getBCFormsByPortfolioId: FormObjectFromJSON[] }>> {
  return apolloClient.query<{ getBCFormsByPortfolioId: FormObjectFromJSON[] }>({
    query: GET_BC_FORMS,
    fetchPolicy: 'no-cache',
    variables: { portfolioId, effectiveDate },
  });
}

export async function requestPortfolioConfigForm(
  portfolioId: string,
  effectiveDate: string,
): Promise<ApolloQueryResult<{ getBrowsingConfigFormByPortfolioId: FormObjectFromJSON[] }>> {
  return apolloClient.query<{ getBrowsingConfigFormByPortfolioId: FormObjectFromJSON[] }>({
    query: GET_BROWSING_PORTFOLIO_CONFIG_FORM,
    fetchPolicy: 'no-cache',
    variables: { portfolioId, effectiveDate },
  });
}

export async function getRateRfqForm(
  id: string,
  portfolioId: string,
  portfolioName: string,
  products: { productId: string; productName: string }[],
  sponsorId: string,
): Promise<ApolloQueryResult<{ getRateRfqForm: FormObjectFromJSON }>> {
  return apolloClient.query<{ getRateRfqForm: FormObjectFromJSON }>({
    query: GET_RATE_RFQ_FORM,
    fetchPolicy: 'no-cache',
    variables: {
      id,
      portfolioId,
      portfolioName,
      products,
      sponsorId,
    },
  });
}

export async function getManageDependentsForm(
  id: string,
  answers: FormAnswersObj,
  dependentId: string | null,
  sponsorId?: string,
): Promise<FormObjectFromJSON> {
  const res = await apolloClient.query<{ getManageDependentsForm: FormObjectFromJSON }>({
    query: GET_MANAGE_DEPENDENTS_FORM,
    fetchPolicy: 'no-cache',
    variables: { id, answers, dependentId, sponsorId },
  });
  return res.data.getManageDependentsForm;
}

export async function cachedRequestFormById(
  id: string,
  bc?: FormBcByProductLine,
): Promise<ApolloQueryResult<{ getFormById: FormObjectFromJSON }>> {
  return apolloClient.query<{ getFormById: FormObjectFromJSON }>({
    query: GET_FORM_BY_ID,
    variables: { id, bc },
  });
}

export async function requestMarkFormsAsInactive(
  periodId: string,
): Promise<FetchResult<{ markFormsAsInactive: boolean }>> {
  return apolloClient.mutate<{ markFormsAsInactive: boolean }>({
    mutation: MARK_FORMS_AS_INACTIVE,
    fetchPolicy: 'no-cache',
    variables: { periodId },
  });
}

export async function getFormInstances(periodId: string, formType: FormType): Promise<FormInstanceType[]> {
  return (
    await apolloClient.query<{ getFormInstances: FormInstanceType[] }>({
      query: GET_FORM_INSTANCES,
      fetchPolicy: 'no-cache',
      variables: { periodId, formType },
    })
  ).data.getFormInstances;
}
